import * as Sentry from "@sentry/react-router";

/**
 * Detects "stale client" version drift: a browser running a cached JS bundle
 * from an older deploy while the server has moved on to a newer version.
 *
 * We compare the version compiled into *this* bundle (`__VERSION__`, baked in at
 * build time) against the live `/api/version` endpoint (the version the server
 * process is actually running). If they differ, the code executing in the
 * browser is stale relative to the backend.
 *
 * Note: we intentionally do NOT use `window.SENTRY_VERSION` here — that value is
 * stamped into the HTML by the *current* server on every render, so for a stale
 * client it would report the new version and hide the drift.
 */

const VERSION_ENDPOINT = "/api/version";
const DEFAULT_INTERVAL_MS = 5 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 10_000;

/** @knipIgnore Public API for a future "reload to update" UI consumer. */
export const VERSION_DRIFT_EVENT = "portal:version-drift";

/** @knipIgnore Public API for a future "reload to update" UI consumer. */
export type VersionDriftDetail = {
  clientVersion: string;
  serverVersion: string;
};

type VersionResponse = {
  version?: string;
};

/** Reported server versions we've already captured, to avoid spamming Sentry. */
const reportedServerVersions = new Set<string>();

let currentDrift: VersionDriftDetail | null = null;

/**
 * Returns the latest detected version drift, or `null` if none.
 *
 * @knipIgnore Public API for a future "reload to update" UI consumer.
 */
export function getVersionDrift(): VersionDriftDetail | null {
  return currentDrift;
}

async function fetchServerVersion(): Promise<string | null> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => {
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(VERSION_ENDPOINT, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (!response.ok) {
      return null;
    }

    const body = (await response.json()) as VersionResponse;
    return body.version ?? null;
  } catch {
    // Network hiccup / navigation abort — not drift, ignore.
    return null;
  } finally {
    window.clearTimeout(timeout);
  }
}

function reportDrift(clientVersion: string, serverVersion: string) {
  const detail: VersionDriftDetail = { clientVersion, serverVersion };
  currentDrift = detail;

  if (!reportedServerVersions.has(serverVersion)) {
    reportedServerVersions.add(serverVersion);

    Sentry.captureMessage("Portal client version drift detected", {
      level: "warning",
      tags: {
        clientVersion,
        serverVersion,
      },
      extra: {
        message: `Client bundle is on ${clientVersion} but server is on ${serverVersion}. The user is likely running a stale cached bundle.`,
      },
    });
  }

  // Let the app surface a "new version available, please reload" prompt if it
  // wants to. Kept decoupled so this module stays UI-agnostic. Fires on every
  // detection; late-mounting listeners should also read `getVersionDrift()` on
  // mount to catch a drift detected before they attached.
  window.dispatchEvent(
    new CustomEvent(VERSION_DRIFT_EVENT, {
      detail,
    }),
  );
}

// A check can be triggered from startup, the interval, and `visibilitychange`
// at once. Without a guard those runs overlap into redundant network requests,
// and — should the check/report path ever gain an `await` between the dedup
// check and add — duplicate drift reports. Serialize so only one check is ever
// in flight; overlapping triggers are dropped (the next interval/visibility
// event will re-check anyway).
let checkInFlight = false;

async function checkOnce(clientVersion: string) {
  if (checkInFlight) {
    return;
  }
  checkInFlight = true;

  try {
    const serverVersion = await fetchServerVersion();
    // A missing/failed response isn't drift — the next interval or visibility
    // event will revalidate anyway, so just wait for it.
    if (!serverVersion || serverVersion === clientVersion) {
      return;
    }

    reportDrift(clientVersion, serverVersion);
  } finally {
    checkInFlight = false;
  }
}

/**
 * Starts polling for client/server version drift. Safe to call once on client
 * startup. Returns a cleanup function that stops all listeners/timers.
 */
export function startVersionDriftCheck(
  intervalMs: number = DEFAULT_INTERVAL_MS,
): () => void {
  const clientVersion = __VERSION__;

  // Without a build-time version there's nothing meaningful to compare against
  // (e.g. local dev), so skip.
  if (!clientVersion) {
    return () => {
      // no-op cleanup when drift checking is disabled
    };
  }

  void checkOnce(clientVersion);

  const interval = window.setInterval(() => {
    void checkOnce(clientVersion);
  }, intervalMs);

  // Re-check when the user returns to the tab — that's the moment a stale bundle
  // is most likely to matter, and cheaper than a tight polling interval.
  const onVisible = () => {
    if (document.visibilityState === "visible") {
      void checkOnce(clientVersion);
    }
  };
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    window.clearInterval(interval);
    document.removeEventListener("visibilitychange", onVisible);
  };
}
